import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { motion, AnimatePresence } from "framer-motion";
import { keyframes } from "@mui/system";
import { useStytchUser } from "@stytch/react";
import { useNavigate } from "react-router-dom";
import {
  logGameEnded,
  logGameShared,
  logAbandonedGame,
  logFeedbackGiven,
} from "../../analytics";
import {
  getStreakFromStorage,
  updateStreak,
  getIsStreakActive,
} from "../../components/StreakUtil";
import { handleAchievementsGameEnded } from "../../components/AchievementsUtil";
import MemoryShareModal from "./MemoryShareModal";
import Footer from "../../components/Footer";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

// Animation keyframes
const flipCard = keyframes`
  0% { transform: rotateY(0); }
  50% { transform: rotateY(90deg); box-shadow: none; }
  100% { transform: rotateY(0); }
`;

const successPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 rgba(145, 178, 209, 0); }
  50% { transform: scale(1.05); box-shadow: 0 0 20px rgba(145, 178, 209, 0.7); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(145, 178, 209, 0); }
`;

// CSS animations
const styles = `
  @keyframes flipAnimation {
    0% { transform: rotateY(0deg); }
    50% { transform: rotateY(90deg); }
    100% { transform: rotateY(180deg); }
  }

  @keyframes unflipAnimation {
    0% { transform: rotateY(180deg); }
    50% { transform: rotateY(90deg); }
    100% { transform: rotateY(0deg); }
  }

  @keyframes matchedPulse {
    0% { box-shadow: 0 0 0 rgba(0, 0, 0, 0); }
    50% { box-shadow: 0 0 20px rgba(236, 176, 97, 0.7); }
    100% { box-shadow: 0 0 0 rgba(0, 0, 0, 0); }
  }

  @keyframes scalePulse {
    0% { transform: scale(1) rotateY(180deg); }
    50% { transform: scale(1.1) rotateY(180deg); }
    100% { transform: scale(1) rotateY(180deg); }
  }

  .card {
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 100%; /* This ensures a square aspect ratio */
    perspective: 1000px;
    cursor: pointer;
    margin-bottom: 2px;
  }

  .card-content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transition: transform 0.6s;
    box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.2);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-front, .card-back {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1.5px solid black;
  }

  .card-front {
    background-color: white;
    transform: rotateY(0deg);
    color: #333;
    font-weight: bold;
  }

  .card-back {
    background-color: white;
    transform: rotateY(180deg);
  }

  .card-flipped .card-content {
    transform: rotateY(180deg);
  }

  .card-matched .card-content {
    box-shadow: 0 0 12px rgba(236, 176, 97, 0.7);
    animation: scalePulse 1.5s, matchedPulse 1.5s;
    animation-delay: 0s; /* Start immediately after match is determined */
    transform: rotateY(180deg) !important; /* Ensure matched cards always show content */
    transition: none; /* Prevent transition when matched */
  }
`;

// Mock data for the memory game
const generateMockData = (customItems) => {
  // Use provided custom items if available, otherwise use default items
  const items = customItems || [
    { id: 1, title: "Dog", image: "🐕" },
    { id: 2, title: "Cat", image: "🐱" },
    { id: 3, title: "Bear", image: "🐻" },
    { id: 4, title: "Fox", image: "🦊" },
    { id: 5, title: "Lion", image: "🦁" },
    { id: 6, title: "Tiger", image: "🐯" },
    { id: 7, title: "Elephant", image: "🐘" },
    { id: 8, title: "Monkey", image: "🐒" },
  ];

  // First, create all cards without pairing them together
  const cards = [];
  items.forEach((item) => {
    if (item.title) {
      // Title-image matching mode
      cards.push({
        id: `${item.id}-title`,
        value: item.id,
        content: item.title,
        type: "title",
        matched: false,
        flipped: false,
      });
      cards.push({
        id: `${item.id}-image`,
        value: item.id,
        content: item.image,
        type: "image",
        isImagePath: item.isImagePath || false,
        matched: false,
        flipped: false,
      });
    } else {
      // Photo-only matching mode - create two separate cards
      const baseProps = {
        value: item.id,
        content: item.image,
        type: "image",
        isImagePath: item.isImagePath || false,
        matched: false,
        flipped: false,
      };

      // Create two separate cards with unique IDs
      cards.push({
        ...baseProps,
        id: `${item.id}-1`,
      });
      cards.push({
        ...baseProps,
        id: `${item.id}-2`,
      });
    }
  });

  // Fisher-Yates shuffle algorithm
  const shuffle = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  // Shuffle multiple times to ensure good distribution
  let shuffledCards = [...cards];
  for (let i = 0; i < 3; i++) {
    shuffledCards = shuffle(shuffledCards);
  }

  return shuffledCards;
};

const MemoryCard = ({ card, onClick, isFlipped, isMatched, logoPath }) => {
  return (
    <Box
      className={`card ${isFlipped ? "card-flipped" : ""} ${
        isMatched ? "card-matched" : ""
      }`}
      onClick={() => !isFlipped && !isMatched && onClick()}
      sx={{
        width: "100%",
        height: "0",
        paddingBottom: "100%", // Creates a square aspect ratio
        position: "relative",
        pointerEvents: isMatched ? "none" : "auto", // Disable clicking on matched cards
      }}
    >
      <Box className="card-content">
        <Box
          className="card-front"
          sx={{
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {logoPath ? (
            <Box
              component="img"
              src={logoPath}
              alt="Card Logo"
              sx={{
                maxWidth: "70%",
                maxHeight: "70%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography variant="h4" sx={{ color: "#333", fontWeight: "bold" }}>
              ?
            </Typography>
          )}
        </Box>
        <Box
          className="card-back"
          sx={{
            backgroundColor: card.type === "title" ? "#ECB061" : "white",
            fontSize:
              card.type === "image"
                ? { xs: "1.5rem", sm: "2rem", md: "2.5rem" }
                : { xs: "0.75rem", sm: "0.9rem", md: "1rem" },
            fontWeight: card.type === "title" ? "bold" : "normal",
            padding: card.type === "title" ? "4px" : "0", // Remove padding for image cards
            wordBreak: "break-word",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          {/* Handle both emoji content and image paths */}
          {card.isImagePath ? (
            <Box
              component="img"
              src={card.content}
              alt="Card Content"
              sx={{
                width: "100%", // Fill the entire width
                height: "100%", // Fill the entire height
                objectFit: "cover", // Cover the whole area
                borderRadius: "4px", // Match card border radius
              }}
            />
          ) : (
            card.content
          )}
        </Box>
      </Box>
    </Box>
  );
};

const BaseMemoryGame = ({
  title = "Memory Match",
  customTitle,
  iconPath,
  logoPath = null,
  subtitle = "Match the word with its image",
  shareText = "I just played Memory Match!",
  shareUrl = window.location.href,
  successBackgroundColor = "#91B2D1",
  shareButtonColor = ["black", "#333"],
  isEmbedded = false,
  bannerPath,
  publicationId = null,
  hideTitle = false,
  customItems = null,
  showDatePicker = true,
}) => {
  const { user } = useStytchUser();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [moves, setMoves] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [streak, setStreak] = useState(
    getStreakFromStorage(publicationId)?.count || 0
  );
  const [isStreakActive, setIsStreakActive] = useState(
    getIsStreakActive(publicationId)
  );
  const [showHint, setShowHint] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [timer, setTimer] = useState(0);
  const [currentDate, setCurrentDate] = useState(new Date());
  const timerRef = useRef(null);
  const gameContainerRef = useRef(null);
  const [showDebugControls, setShowDebugControls] = useState(false);

  // Insert CSS styles on component mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Initialize game
  useEffect(() => {
    resetGame();
  }, []);

  // Timer logic
  useEffect(() => {
    if (gameStarted && !gameOver) {
      timerRef.current = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameStarted, gameOver]);

  // Check for game completion
  useEffect(() => {
    if (matchedPairs === cards.length / 2 && cards.length > 0) {
      endGame(true);
    }
  }, [matchedPairs, cards.length]);

  // Track game abandonment
  useEffect(() => {
    const handleUnload = () => {
      if (gameStarted && !gameOver && moves > 0) {
        logAbandonedGame(title, publicationId, {
          moves,
          matchedPairs,
          totalPairs: cards.length / 2,
        });
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [
    gameStarted,
    gameOver,
    moves,
    matchedPairs,
    cards.length,
    publicationId,
    title,
  ]);

  const resetGame = () => {
    const newCards = generateMockData(customItems);
    setCards(newCards);
    setFlippedCards([]);
    setMatchedPairs(0);
    setMoves(0);
    setGameOver(false);
    setGameStarted(false);
    setTimer(0);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const handleCardClick = (cardId) => {
    // Start the game on first card click
    if (!gameStarted) {
      setGameStarted(true);
    }

    // Don't allow more than 2 cards to be flipped at once or clicking on already matched cards
    if (flippedCards.length >= 2) return;

    // Update the flipped status in the cards array
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.id === cardId ? { ...card, flipped: true } : card
      )
    );

    // Add the flipped card to flippedCards
    setFlippedCards((prev) => [...prev, cardId]);
  };

  // Process flipped cards logic
  useEffect(() => {
    // If we have 2 flipped cards, check for a match
    if (flippedCards.length === 2) {
      setMoves((prev) => prev + 1);

      const [firstId, secondId] = flippedCards;
      const firstCard = cards.find((card) => card.id === firstId);
      const secondCard = cards.find((card) => card.id === secondId);

      // Check if they match (have the same value)
      if (firstCard && secondCard && firstCard.value === secondCard.value) {
        // Wait to let the second card's flip animation complete before marking as matched
        // This ensures the user sees both cards flipped before we show the match animation
        setTimeout(() => {
          // It's a match - update matched status after we've seen the card flip
          setMatchedPairs((prev) => prev + 1);

          // Update the matched cards - keeping them flipped and marking as matched
          setCards((prevCards) =>
            prevCards.map((card) => {
              if (card.id === firstId || card.id === secondId) {
                return { ...card, matched: true, flipped: true };
              }
              return card;
            })
          );

          // Clear flipped cards array for next pair
          setFlippedCards([]);
        }, 800); // Slightly longer delay to ensure flip animation is complete
      } else {
        // Not a match - flip them back after a delay
        setTimeout(() => {
          setCards((prevCards) =>
            prevCards.map((card) =>
              card.id === firstId || card.id === secondId
                ? { ...card, flipped: false }
                : card
            )
          );
          setFlippedCards([]);
        }, 1000);
      }
    }
  }, [flippedCards, cards]);

  const endGame = (success) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setGameOver(true);

    // Update streak - safely handle potential undefined values
    let newStreak = streak;
    if (publicationId) {
      try {
        newStreak = updateStreak(new Date(), publicationId) || 0;
        setStreak(newStreak);
        setIsStreakActive(true);
      } catch (error) {
        console.error("Error updating streak:", error);
        // Don't update the streak if there's an error
      }
    }

    // Safely handle analytics logging
    try {
      // Log game ended with safe defaults for any undefined values
      logGameEnded(title || "Memory Match", publicationId, {
        won: success || false,
        moves: moves || 0,
        matchedPairs: matchedPairs || 0,
        totalPairs: cards.length / 2 || 0,
        timeSeconds: timer || 0,
        streak: newStreak || 0,
      });

      // Handle achievements
      handleAchievementsGameEnded({
        solved: success || false,
        attempts: moves || 0,
        maxAttempts: cards.length || 16, // Default to 16 if undefined
        streakCount: newStreak || 0,
        publicationId: publicationId || null,
      });
    } catch (error) {
      console.error("Error logging game analytics:", error);
      // Continue even if analytics logging fails
    }

    // Show share modal with a slight delay
    setTimeout(() => {
      setShareModalOpen(true);
    }, 1000);
  };

  const handleShare = () => {
    setShareModalOpen(true);
  };

  const handleShareConfirm = () => {
    // Create a shareable text with game results in a more direct format
    const gameTitle = customTitle || title || "Memory Match";
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    // Create a more direct and concise share text
    const shareString =
      `${gameTitle} ${dateStr}\n` +
      `Moves: ${moves} | Time: ${formatTime(timer)}\n\n` +
      `${shareUrl}`;

    // Try to copy the text to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(shareString)
        .then(() => {
          // Log share analytics
          logGameShared(title, publicationId, {
            won: true,
            moves,
            matchedPairs,
            totalPairs: cards.length / 2,
            timeSeconds: timer,
          });
        })
        .catch((err) => {
          console.error("Share failed:", err);
          alert(
            "Sorry, couldn't copy to clipboard. Please copy this manually:\n\n" +
              shareString
          );
        });
    } else {
      // Fallback for browsers that don't support clipboard API
      try {
        // Create a temporary textarea element
        const textArea = document.createElement("textarea");
        textArea.value = shareString;

        // Make the textarea out of viewport
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);

        // Select and copy the text
        textArea.focus();
        textArea.select();
        document.execCommand("copy");

        // Remove the textarea
        document.body.removeChild(textArea);

        // Log share analytics
        logGameShared(title, publicationId, {
          won: true,
          moves,
          matchedPairs,
          totalPairs: cards.length / 2,
          timeSeconds: timer,
        });
      } catch (err) {
        console.error("Share failed (fallback):", err);
        alert(
          "Sorry, couldn't copy to clipboard. Please copy this manually:\n\n" +
            shareString
        );
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const onRateGame = (isPositiveRating) => {
    logFeedbackGiven(title, isPositiveRating, publicationId, {
      won: gameOver && matchedPairs === cards.length / 2,
      moves,
      timeSeconds: timer,
    });
  };

  // Force win function - matches all cards immediately
  const handleForceWin = () => {
    // Set all cards to flipped and matched
    setCards((prevCards) =>
      prevCards.map((card) => ({ ...card, flipped: true, matched: true }))
    );

    // Set matched pairs to total number of pairs
    setMatchedPairs(cards.length / 2);

    // End the game with success
    endGame(true);
  };

  const renderGameBoard = () => {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          aspectRatio: "1 / 1", // Keep the board square
          mx: "auto",
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gridTemplateRows: "repeat(4, 1fr)",
          gap: 1,
        }}
      >
        {cards.map((card) => (
          <Box
            key={card.id}
            sx={{
              width: "100%",
              height: "100%",
              aspectRatio: "1 / 1", // Each card is square and fills cell
              position: "relative",
            }}
          >
            <MemoryCard
              card={card}
              onClick={() => handleCardClick(card.id)}
              isFlipped={card.flipped}
              isMatched={card.matched}
              logoPath={logoPath}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const renderGameControls = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          width: "100%",
          maxWidth: "100%",
          px: 1,
        }}
      >
        <Box sx={{ textAlign: "left", flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Moves: {moves}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right", flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Time: {formatTime(timer)}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderDatePicker = () => {
    if (!showDatePicker) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
          position: "relative",
          minHeight: 40,
        }}
      >
        {/* Date navigation */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            disabled={true}
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            tabIndex={-1}
          >
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2, color: "black" }}>
            {currentDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </Typography>
          <IconButton
            disabled={true}
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            tabIndex={-1}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        p: 2,
        mt: hideTitle ? 2 : 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        position: "relative",
        flex: 1,
        height: "100%",
        minHeight: 0,
      }}
      ref={gameContainerRef}
      id="memory-game-container"
    >
      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        {!hideTitle && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: "bold",
                textAlign: "center",
                mb: 1,
              }}
            >
              {customTitle || title}
            </Typography>
            {subtitle && (
              <Typography
                variant="subtitle1"
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        {renderDatePicker()}

        <AnimatePresence>
          {!!errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: "#ffebee",
                  width: { xs: "80%", md: "50%" },
                  mx: "auto",
                }}
              >
                <Typography color="error">{errorMessage}</Typography>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {renderGameControls()}
        {renderGameBoard()}

        {gameOver && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleShare}
              sx={{ backgroundColor: "white" }}
            >
              Share Results
            </Button>
          </Box>
        )}

        {/* Testing controls - only visible in development mode */}
        {showDebugControls && (
          <Box
            sx={{
              mt: 4,
              pt: 2,
              borderTop: "1px dashed #ccc",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight="bold"
            >
              Testing:
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                color="success"
                size="small"
                onClick={handleForceWin}
              >
                Force Win
              </Button>
            </Box>
          </Box>
        )}

        {shareModalOpen && (
          <MemoryShareModal
            open={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            onShare={handleShareConfirm}
            matchedPairs={matchedPairs}
            totalPairs={cards.length / 2}
            moves={moves}
            timeFormatted={formatTime(timer)}
            successBackgroundColor={successBackgroundColor}
          />
        )}
      </Box>
    </Box>
  );
};

export default BaseMemoryGame;
